"use client";

import React from "react";
import { ArrowRight, Compass, ClipboardCheck, MapPin } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const trustBadges = [{
    icon: MapPin,
    label: "Decatur & Surrounding Areas"
  }, {
    icon: ClipboardCheck,
    label: "Clear Written Scope"
  }, {
    icon: Compass,
    label: "Design Through Completion"
  }];
  return <section id="hero" data-nav="light" className="relative min-h-[85vh] w-full overflow-hidden bg-background flex items-center pt-24 pb-16 sm:pt-32 sm:pb-24 lg:pt-36 lg:pb-28">
      {/* Background Architectural Photography */}
      <div className="absolute inset-0 z-0 select-none">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_home_remodel_hero.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_home_remodel_hero.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_home_remodel_hero.webp-wvc-sizes" alt="Sunlit open concept whole home interior remodel in Decatur Georgia" loading="eager" decoding="async" className="h-full w-full object-cover object-center" />
        {/* Soft Warm Ivory & Slate Ambient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/85 to-background/40" />
        <div className="absolute inset-0 bg-background/20 backdrop-blur-[1px]" />
      </div>

      {/* Hero Content Container */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* Main Asymmetrical Frosted Editorial Card */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-8 xl:col-span-7 rounded-2xl border border-border bg-card/90 p-6 sm:p-10 lg:p-12 shadow-xl backdrop-blur-md">
            {/* Architectural Copper Accent Rule */}
            <div className="mb-6 flex items-center gap-3">
              <span className="h-0.5 w-8 rounded-full bg-primary" />
              <Badge variant="secondary" className="bg-secondary text-secondary-foreground border border-border/80 px-3 py-1 text-xs font-semibold tracking-wider uppercase rounded-sm">
                Decatur Residential Renovations
              </Badge>
            </div>

            {/* Display Heading */}
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.15]">
              Whole-home and room remodeling in Decatur
            </h1>

            {/* Editorial Supporting Description */}
            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans max-w-2xl">
              From open-plan layout reconfigurations and master additions to complete interior structural transformations and finished basements, we build with precision, architectural harmony, and enduring craft.
            </p>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-sans font-semibold px-6 py-6 shadow-sm transition-[background-color,box-shadow]">
                <Link to="#" className="inline-flex items-center justify-center gap-2">
                  Request an estimate
                  <ArrowRight className="size-4.5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border bg-background/80 text-foreground hover:bg-secondary hover:text-foreground rounded-md font-sans font-semibold px-6 py-6 transition-[background-color,border-color]">
                <Link to="#">
                  View completed projects
                </Link>
              </Button>
            </div>

            {/* Architectural Trust Points Divider */}
            <div className="mt-10 pt-8 border-t border-border/80">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {trustBadges.map((item, i) => {
                const Icon = item.icon;
                return <div key={item.label} data-index={i} className="flex items-center gap-3">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-secondary text-primary border border-border/60">
                        <Icon className="size-4" />
                      </div>
                      <span className="text-xs sm:text-sm font-medium text-foreground/90 leading-snug">
                        {item.label}
                      </span>
                    </div>;
              })}
              </div>
            </div>
          </motion.div>

          {/* Complementary Architectural Floating Detail */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15,
          ease: [0.16, 1, 0.3, 1]
        }} className="hidden lg:flex lg:col-span-4 xl:col-span-5 flex-col justify-end">
            <div className="rounded-xl border border-border/80 bg-card/80 p-6 backdrop-blur-md shadow-lg max-w-sm ml-auto">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Local Focus
              </p>
              <h2 className="mt-1 font-serif text-xl font-semibold text-card-foreground">
                Oakhurst, Winnona Park & Great Lakes
              </h2>
              <p className="mt-2 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Tailoring structural updates and historic charm for Decatur residences since day one.
              </p>
              <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span>Direct consultation</span>
                <span className="font-semibold text-foreground">Decatur, GA 30030</span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}